import { applySnapshot, flow, types, getSnapshot } from "mobx-state-tree";

import createTableStore, { DEFAULT_PAGE_SIZE } from "./templates/TableStore";

import api from "services/api";

import { PUBLISH_STATUSES } from "constants/models";
import { FILTER_COLUMNS } from "constants/filterConstants";
import { isEmptyObject } from "utils";
import { MSURL } from "utils/ms";
import { getNextPublishStatusWithoutScheduled } from "../utils/publish";

const pathQuiz = "/quiz/api/v1";
export const QuestionModel = types.model({
  id: types.number,
  title: types.string,
  code: types.string,
  complement: types.string,
  responseType: types.maybeNull(types.string),
  isRequired: types.boolean,
  publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
});

export const QuizModel = types
  .model({
    id: types.number,
    title: types.string,
    code: types.string,
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
      };
    },
  }));

export const QuizDetailModel = types
  .model({
    id: types.number,
    title: types.string,
    code: types.string,
    publishStatus: types.enumeration(Object.values(PUBLISH_STATUSES)),
    questions: types.array(QuestionModel),
  })
  .views(self => ({
    get formData() {
      return {
        ...self,
      };
    },
  }));

const QuestionnaireQuizStore = createTableStore({
  fetch: flow(function*({ params }) {
    return api.get(`${pathQuiz}/quiz`, params, undefined, MSURL);
  }),
  initialOrder: { column: "created", desc: true },
  itemModel: QuizModel,
  searchBy: "search",
})
  .props({
    details: types.maybe(QuizDetailModel),
    isMoreLoading: types.optional(types.boolean, false),
    isQuizLoading: types.optional(types.boolean, false),
    isQuizManaging: types.optional(types.boolean, false),
    isQuizPublishing: types.optional(types.boolean, false),
    isQuizCopying: types.optional(types.boolean, false),
  })
  .views(self => ({
    get hasMoreInfo() {
      return !!self.totalPages && self.page !== self.totalPages;
    },

    get quizOptions() {
      const { items } = getSnapshot(self);
      return items.map(({ id, title, code, publishStatus }) => ({
        label: title,
        value: {
          id,
          title,
          code,
          publishStatus,
        },
      }));
    },
  }))
  .actions(self => ({
    updateQuiz(id: number, data: any) {
      const filtersContainNewStatus =
        isEmptyObject(self.filters) ||
        self.filters[FILTER_COLUMNS.PUBLISH_STATUS]?.includes(
          data.publishStatus,
        );

      if (!filtersContainNewStatus) {
        const filteredItems = self.items.filter(item => item.id !== id);
        applySnapshot(self, { ...self, items: filteredItems });
      } else {
        const index = self.items.findIndex(item => item.id === id);
        self.items[index] = { ...self.items[index], ...data };
      }
    },
  }))
  .actions(self => ({
    fetchMoreQuizzes: flow(function*() {
      self.isMoreLoading = true;

      try {
        if (self.hasMoreInfo) {
          const { orderBy, orderByDesc } = self.orderContext || {};

          self.applyPage(self.page + 1);

          const { items } = yield api.get(
            `${pathQuiz}/quiz`,
            {
              size: DEFAULT_PAGE_SIZE,
              page: self.page,
              search: self.search,
              orderBy,
              orderByDesc,
              ...self.filters,
            },
            undefined,
            MSURL,
          );

          applySnapshot(self, {
            ...self,
            items: self.items.concat(items),
          });
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isMoreLoading = false;
      }
    }),

    fetchQuizDetails: flow(function*(id?: number) {
      self.isQuizLoading = true;

      try {
        const details = yield api.get(
          `${pathQuiz}/quiz/${id}`,
          undefined,
          undefined,
          MSURL,
        );

        applySnapshot(self, {
          ...self,
          details,
        });
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuizLoading = false;
      }
    }),

    createQuiz: flow(function*(payload: any) {
      self.isQuizManaging = true;

      try {
        const { id } = yield api.post(
          `${pathQuiz}/quiz`,
          payload,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuizManaging = false;
      }
    }),

    editQuizInfo: flow(function*(payload: any) {
      self.isQuizManaging = true;

      try {
        return yield api.put(
          `${pathQuiz}/quiz/${payload.id}`,
          payload,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuizManaging = false;
      }
    }),

    changeQuizStatus: flow(function*(id: number, status: string) {
      self.isQuizPublishing = true;

      try {
        const newStatus = getNextPublishStatusWithoutScheduled(status);
        if (newStatus) {
          const { success } = yield api.post(
            `${pathQuiz}/quiz/${id}/status`,
            {
              status: newStatus,
            },
            undefined,
            MSURL,
          );
          if (success) {
            self.updateQuiz(id, { publishStatus: newStatus });
          }
        }
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuizPublishing = false;
      }
    }),

    addQuestionToQuiz: flow(function*(payload: any) {
      self.isQuizManaging = true;

      try {
        const { id } = yield api.post(
          `${pathQuiz}/quiz/${payload.id}/add-question`,
          payload.addQuestion,
          undefined,
          MSURL,
        );
        return id;
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuizManaging = false;
      }
    }),

    updateQuestionOrder: flow(function*(payload: any) {
      self.isQuizLoading = true;

      try {
        return yield api.post(
          `${pathQuiz}/quiz/${payload.id}/update-order-question`,
          payload.newOrder,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuizLoading = false;
      }
    }),

    deleteQuestion: flow(function*(payload: any) {
      self.isQuizLoading = true;

      try {
        return yield api.delete(
          `${pathQuiz}/quiz/${payload.id}/question/${payload.questionID}/delete`,
          undefined,
          undefined,
          undefined,
          MSURL,
        );
      } catch (error) {
        console.log("error :>> ", error);
        throw error;
      } finally {
        self.isQuizLoading = false;
      }
    }),

    resetDetails() {
      applySnapshot(self, { ...self, details: undefined });
    },
  }));

export default QuestionnaireQuizStore.create();
